import { usePricingContext } from '@vp/react-pricing-context';
import { useEffect, useState } from 'react';
import {
    Combobox,
    ComboboxInput,
    ComboboxList,
    ComboboxOption,
    ComboboxPopover,
    ComboboxSection,
} from '@vp/swan';
import { Currency, bugtracker, price, useIdentityContext } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { sendProductSelectedTrackingEvent } from '@99designs/tracking';
import { useCart } from '../../../../../lib/hooks/useCart';
import { useBriefContext } from '../../../../BriefContext/BriefContext';
import { useBriefFormContext } from '../../../../BriefContext/BriefFormContext';
import { useProductOptions } from '../ProductOptions';
import { ClientConfiguredFieldProps } from '../types';
import { useServiceProductDetailsLazyQuery, useTopProductsQuery } from './products.generated';
import useUpdateProductDropdown from './useUpdateProductDropdown';
import {
    Product,
    RecentlySelectedProduct,
    categoryBriefMap,
    mapCategoriesToProducts,
    transformBriefProductToProduct,
    transformProductToBriefProduct,
} from './utils';

export function Input({
    id,
    clientConfiguredValue,
    setValue,
    register,
    clearErrors,
}: ClientConfiguredFieldProps) {
    const { locale, pageData } = usePage();
    const { setSelectedOptions, setProductOptionTouched } = useProductOptions();

    const { accessToken, shopperId, isSignedIn } = useIdentityContext();
    const {
        product: productFromBriefContext,
        recentProducts,
        addRecentProduct,
        isUpdatingProduct,
        setIsUpdatingProduct,
    } = useBriefContext();
    const { briefId } = useBriefFormContext();
    const pricingContext = usePricingContext();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [selectedKey, setSelectedKey] = useState<string>(clientConfiguredValue.toLowerCase());

    // MUTATIONS

    const updateMutation = useUpdateProductDropdown(id, briefId, clientConfiguredValue);

    // QUERIES

    const [getServiceProductDetails] = useServiceProductDetailsLazyQuery({ errorPolicy: 'all' });
    const { data, loading: isTopProductsQueryLoading } = useTopProductsQuery();
    const { isInCart } = useCart(locale, { accessToken, shopperId });

    useEffect(() => {
        async function disableIfBriefIsInCart() {
            const briefInCart = await isInCart(briefId as string);
            setIsDisabled(briefInCart);
        }
        if (isSignedIn) {
            void disableIfBriefIsInCart();
        }
    }, [briefId, isInCart, isSignedIn]);

    // LOGIC

    useEffect(() => {
        if (
            productFromBriefContext &&
            !recentProducts
                ?.map((product) => product.mpvId.toLowerCase())
                .includes(productFromBriefContext.fulfilmentMpvId?.toLowerCase() || '')
        ) {
            addRecentProduct(transformBriefProductToProduct(productFromBriefContext));
        }
    }, [addRecentProduct, productFromBriefContext, recentProducts]);

    useEffect(() => {
        setIsLoading(isTopProductsQueryLoading || isUpdatingProduct);
    }, [isTopProductsQueryLoading, isUpdatingProduct]);

    useEffect(() => {
        setValue(id, clientConfiguredValue.toLowerCase());
        clearErrors(id);
    }, [id, clientConfiguredValue, setValue, clearErrors]);

    if (!data) {
        return (
            <Combobox mb="5" key="loading-dropdown-msb">
                <ComboboxInput placeholder={__('Select product')} loadingShimmer />
            </Combobox>
        );
    }

    const [sortedCategoryMap, productMap] = mapCategoriesToProducts(data);

    const handleChange = async (selection: string) => {
        if (!selection) return;

        setIsUpdatingProduct(true);

        const maybeTopProductHit = productMap.get(selection);
        const selectedProduct = maybeTopProductHit
            ? maybeTopProductHit
            : recentProducts?.find(
                  (product: RecentlySelectedProduct) =>
                      product.mpvId.toLowerCase() === selection.toLowerCase()
              );

        if (!selectedProduct) {
            bugtracker.notify(new Error('cannot get selected product'), (event) => {
                event.addMetadata('debug info', 'mpvID', selection);
            });

            return null;
        }

        addRecentProduct(selectedProduct);

        const { data: productDetails, error } = await getServiceProductDetails({
            variables: {
                mpvId: selection,
            },
        });

        if (error)
            bugtracker.notify(
                new Error(
                    `Product Details unable to be retrieved. Error message: ${error.message}`
                ),
                (event) => {
                    event.addMetadata('debug info', 'mpvID', selection);
                }
            );

        const updatedProduct = transformProductToBriefProduct(
            selectedProduct,
            productDetails?.serviceDetailPage.inclusions
        );

        if (updatedProduct) {
            await updateMutation(selection);

            setSelectedOptions({});
            setProductOptionTouched();

            sendProductSelectedTrackingEvent({
                ...pageData,
                category: 'Design Services',
            });
        }

        setSelectedKey(selection);

        setIsUpdatingProduct(false);
        return;
    };
    const comboBoxItems = [
        ...Array.from(sortedCategoryMap.keys()).map((category) => (
            <ComboboxSection title={categoryBriefMap(category) || category} key={category}>
                {sortedCategoryMap
                    .get(category)
                    ?.map((product: Product) => (
                        <ComboboxOption key={product.mpvId.toLowerCase()}>
                            {productOptionLabel(product, pricingContext?.vatInclusive)}
                        </ComboboxOption>
                    )) ?? []}
            </ComboboxSection>
        )),
        <ComboboxSection title={__('Recently Selected')} key={'recently-selected'}>
            {recentProducts?.map((product: RecentlySelectedProduct) => (
                <ComboboxOption key={product.mpvId.toLowerCase()}>
                    {productOptionLabel(product, pricingContext?.vatInclusive)}
                </ComboboxOption>
            )) ?? []}
        </ComboboxSection>,
    ];

    return (
        <Combobox
            mb="5"
            {...register(id, {
                required: __('Please select a product type', {
                    _context: 'This is the error message for product selection',
                }),
            })}
            selectedKey={selectedKey}
            onSelectionChange={(selection) => handleChange((selection as string).toLowerCase())}
            menuTrigger="focus"
        >
            <ComboboxInput
                placeholder={__('Select product')}
                loadingShimmer={isLoading}
                disabled={isDisabled || isLoading}
                data-testid="product-dropdown"
                aria-required={true}
                aria-labelledby={id}
            />
            <ComboboxPopover>
                <ComboboxList>{comboBoxItems}</ComboboxList>
            </ComboboxPopover>
        </Combobox>
    );
}

function productOptionLabel(product: Product | RecentlySelectedProduct, includeTax?: boolean) {
    const p = product.price(includeTax);
    const formattedPrice = price({
        amount: p.amountInCents,
        currency: p.currency as Currency,
        decimalPlaces: 2,
    });
    return `${product.name} - ${formattedPrice}`;
}
